import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import * as api from '@/features/backlog/api';
import { boardPath, viewKeyOf } from '@/constants/routes';
import { Board, Member } from '@/features/backlog/types';

type Ctx = {
  boards: Board[];
  members: Member[];
  currentBoardId: string | null;
  currentBoard: Board | null;
  /** Switch to a board by id — navigates to its friendly URL, keeping the current view. */
  setCurrentBoardId: (id: string) => void;
  loading: boolean;
  error: string | null;
  reloadBoards: () => Promise<void>;
  reloadMembers: () => Promise<void>;
};

const AppDataContext = createContext<Ctx | null>(null);
// Remembers the last board the user was on, so the boards index can offer a quick "continue".
const LAST_SLUG_KEY = 'spark-board.lastBoardSlug';

export const useAppData = (): Ctx => {
  const v = useContext(AppDataContext);
  if (!v) throw new Error('useAppData must be used within AppDataProvider');
  return v;
};

/**
 * Loads boards + members, and derives the CURRENT board from the URL slug (the friendly routes
 * make the path the single source of truth: /:slug is the board, no UUIDs). Switching board is a
 * navigation. Must sit inside the Router (it reads the location).
 */
export const AppDataProvider = ({ children }: { children: ReactNode }) => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const reloadBoards = useCallback(async () => {
    setBoards(await api.listBoards());
  }, []);

  const reloadMembers = useCallback(async () => {
    setMembers(await api.listMembers());
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await Promise.all([reloadBoards(), reloadMembers()]);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [reloadBoards, reloadMembers]);

  // The URL owns the current board: the first path segment is the board slug (the boards index at
  // "/" and the global "/members" resolve to no board). The 2nd segment (if any) is the view or an
  // item key — kept only to preserve the view when switching boards.
  const segments = location.pathname.split('/').filter(Boolean);
  const slug = segments[0] ? decodeURIComponent(segments[0]) : '';
  const currentView = viewKeyOf(segments[1]);
  const currentBoard = useMemo(() => boards.find((b) => b.slug === slug) ?? null, [boards, slug]);
  const currentBoardId = currentBoard?.id ?? null;

  // Remember the last real board for the index's "continue" shortcut.
  useEffect(() => {
    if (currentBoard) {
      try {
        localStorage.setItem(LAST_SLUG_KEY, currentBoard.slug);
      } catch {
        /* ignore unavailable storage */
      }
    }
  }, [currentBoard]);

  const setCurrentBoardId = useCallback(
    (id: string) => {
      const b = boards.find((x) => x.id === id);
      if (b) navigate(boardPath(b.slug, currentView));
    },
    [boards, navigate, currentView],
  );

  const value = useMemo<Ctx>(
    () => ({
      boards,
      members,
      currentBoardId,
      currentBoard,
      setCurrentBoardId,
      loading,
      error,
      reloadBoards,
      reloadMembers,
    }),
    [boards, members, currentBoardId, currentBoard, setCurrentBoardId, loading, error, reloadBoards, reloadMembers],
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

/** The remembered last board slug (for the boards index "continue" shortcut). */
export const lastBoardSlug = (): string | null => {
  try {
    return localStorage.getItem(LAST_SLUG_KEY);
  } catch {
    return null;
  }
};
